// resource_type passed through to BE; backend enforces 7-val ACL polymorphism.
// Caller specifies action columns; component does not assume action set.
// Z-index: dialog at --z-modal; Combobox inside ApproverPicker portals above
// per ladder (--z-dropdown=60 > --z-modal=50) — no custom portal needed.

import { useTranslations } from "use-intl";
import { Shield } from "lucide-react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import {
  ResourcePermissionMatrix,
  type ActionDef,
} from "./resource-permission-matrix";
import { PermissionHelpButton } from "./permission-help-dialog";

export interface ResourceShareDialogProps {
  resourceType: string;
  resourceId: string;
  actions: ActionDef[];
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

/**
 * ResourceShareDialog — generalized ACL modal.
 * Wraps ResourcePermissionMatrix in a Modal shell.
 * Used for workflow share (from WorkflowHeader Share button) and any future
 * resource type (knowledge, connector, etc.) without code changes — pass the
 * matching resourceType + actions array.
 */
export function ResourceShareDialog({
  resourceType,
  resourceId,
  actions,
  isOpen,
  onClose,
  title,
}: ResourceShareDialogProps) {
  const t = useTranslations("permissions");

  const dialogTitle =
    title ?? t("share.title");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      aria-label={dialogTitle}
    >
      <ModalHeader>
        <div className="flex items-start justify-between gap-3 pr-10">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-[var(--primary-50)] dark:bg-[var(--primary-50)]">
              <Shield className="size-5 text-[var(--primary)]" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-[var(--foreground)]">
                {dialogTitle}
              </h2>
              <p className="text-[12px] text-[var(--muted-foreground)] mt-0.5">
                {resourceType}
              </p>
            </div>
          </div>
          <PermissionHelpButton />
        </div>
      </ModalHeader>

      <ModalBody className="overflow-y-auto max-h-[60vh]">
        <ResourcePermissionMatrix
          resourceType={resourceType}
          resourceId={resourceId}
          actions={actions}
        />
      </ModalBody>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          {t("share.done")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ResourceShareDialog;
